import { Link } from 'react-router-dom'
import { Heart, Wallet, Users, ChevronRight, Activity, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DetailsSummaryProps {
  /** Whether the summary is expanded */
  expanded: boolean
  /** Health status summary */
  health?: {
    status: 'good' | 'unknown' | 'concern'
    metricCount: number
    description: string
  }
  /** Financial summary */
  financial?: {
    spending: number
    period: string
  }
  /** Family summary */
  family?: {
    memberCount: number
    familyName: string
  }
  /** Whether data is still loading */
  isLoading?: boolean
  className?: string
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

function SummaryItem({
  icon: Icon,
  label,
  value,
  description,
  href,
  status,
}: {
  icon: typeof Heart
  label: string
  value: string
  description: string
  href: string
  status?: 'good' | 'unknown' | 'concern'
}) {
  const statusColors = {
    good: 'text-success-600',
    unknown: 'text-gray-400',
    concern: 'text-warning-600',
  }

  return (
    <Link
      to={href}
      className="hearth-card hearth-press flex items-center gap-4 p-4"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-500/10">
        <Icon className={cn('h-5 w-5', status ? statusColors[status] : 'text-gray-400')} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {label}
        </p>
        <p className="mt-0.5 font-rounded text-lg font-semibold tabular-nums text-gray-900 truncate">
          {value}
        </p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <ChevronRight className="h-5 w-5 text-gray-300" />
    </Link>
  )
}

function SummarySkeleton() {
  return (
    <div className="hearth-card flex items-center gap-4 p-4 animate-pulse">
      <div className="h-10 w-10 rounded-full bg-gray-200" />
      <div className="flex-1">
        <div className="h-3 w-16 bg-gray-200 rounded" />
        <div className="mt-2 h-5 w-24 bg-gray-200 rounded" />
        <div className="mt-1 h-3 w-20 bg-gray-200 rounded" />
      </div>
    </div>
  )
}

export function DetailsSummary({
  expanded,
  health,
  financial,
  family,
  isLoading,
  className,
}: DetailsSummaryProps) {
  if (!expanded) {
    return null
  }

  const healthValue = health?.status === 'good'
    ? 'Looking good'
    : health?.status === 'concern'
      ? 'Worth checking'
      : 'No data yet'

  return (
    <div
      className={cn(
        'grid gap-3 sm:grid-cols-2 lg:grid-cols-3',
        className
      )}
    >
      {isLoading ? (
        <>
          <SummarySkeleton />
          <SummarySkeleton />
          <SummarySkeleton />
        </>
      ) : (
        <>
          <SummaryItem
            icon={health?.metricCount ? Activity : Heart}
            label="Health"
            value={healthValue}
            description={health?.description || 'Connect a health source'}
            href="/health"
            status={health?.status}
          />
          <SummaryItem
            icon={financial?.spending ? TrendingUp : Wallet}
            label="Spending"
            value={financial ? formatCurrency(financial.spending) : '--'}
            description={financial?.period || 'No accounts connected'}
            href="/financial"
          />
          <SummaryItem
            icon={Users}
            label="Family"
            value={family ? `${family.memberCount} members` : '--'}
            description={family?.familyName || 'Your family'}
            href="/family"
          />
        </>
      )}
    </div>
  )
}
